import type { ComputedRef, Ref } from 'vue';
// packages/studio/app/composables/use-form-submit-handler.ts
import type { FormSchema } from '#layers/block-engine';
import { computed, ref } from 'vue';

export interface RawFormDataSource<T = Record<string | symbol, unknown>> {
	/** Return public backing form data without invoking relation accessors. */
	$getRawData: (options?: { clone?: boolean }) => Partial<T>;
}

/**
 * Interface defining the contract for RStore form objects.
 * These $ prefixed methods are provided by RStore's form system.
 * Extends a generic T (typically AnyItem) to allow indexing by data field names.
 */
export type RStoreFormObject<T = Record<string | symbol, unknown>> = T & RawFormDataSource<T> & {
	$hasChanges?: () => boolean;
	/** Read a backing form field without invoking relation accessors. */
	$getRaw: (field: PropertyKey) => unknown;
	$submit?: () => Promise<T>;
	$reset?: () => void;
	$loading?: boolean;
	$error?: Error | null;
	$schema?: FormSchema;
};

export interface UseFormSubmitHandlerOptions<T> {
	/** Called before submit attempt */
	onBeforeSubmit?: () => void;

	/** Called on successful submit (can be async) */
	onSuccess?: (item: T) => void | Promise<void>;

	/** Called on error */
	onError?: (error: Error) => void;

	/** Whether this is create mode (affects submitDisabled logic) */
	isCreate?: ComputedRef<boolean>;
}

export interface UseFormSubmitHandlerReturn {
	onSubmit: () => Promise<void>;
	submitDisabled: ComputedRef<boolean>;
	submitError: Ref<Error | null>;
	isSubmitting: Ref<boolean>;
}

export function useFormSubmitHandler<T>(
	formObject: Ref<RStoreFormObject<T> | null>,
	options?: UseFormSubmitHandlerOptions<T>,
): UseFormSubmitHandlerReturn {
	const submitError = ref<Error | null>(null);
	const isSubmitting = ref(false);

	const submitDisabled = computed(() => {
		if (!formObject.value)
			return true;

		// In update mode, disable if no changes
		if (options?.isCreate?.value === false) {
			return !formObject.value.$hasChanges?.();
		}

		return false;
	});

	async function onSubmit() {
		if (submitDisabled.value || isSubmitting.value)
			return;

		isSubmitting.value = true;

		try {
			options?.onBeforeSubmit?.();

			const result = await formObject.value?.$submit?.();

			await options?.onSuccess?.(result as T);

			// Clear error on success
			submitError.value = null;
		}
		catch (error) {
			options?.onError?.(error as Error);

			// Capture error from either $submit() or onSuccess callback
			submitError.value = (error as Error) ?? formObject.value?.$error ?? null;
		}
		finally {
			isSubmitting.value = false;
		}
	}

	return { onSubmit, submitDisabled, submitError, isSubmitting };
}
